<script>
import { mapActions, mapGetters } from 'vuex'
import formatDateMixin from '@/mixins/formatDateMixin'

export default {
  name: 'InvoicesList',
  mixins: [formatDateMixin],
  data() {
    return {
      headers: [
        {
          text: this.$t('finance.invoicePaidStatusHeader'),
          sortable: false,
          value: 'paidStatus',
        },
        {
          text: this.$t('finance.invoiceNumberHeader'),
          sortable: false,
          value: 'invoiceNumber',
          align: 'right',
        },
        {
          text: this.$t('finance.invoiceDateHeader'),
          sortable: false,
          value: 'invoiceDateUTC',
        },
        {
          text: this.$t('finance.invoicePaidDateHeader'),
          sortable: false,
          value: 'datePaidUTC',
        },
        {
          text: this.$t('finance.invoiceIssuedToHeader'),
          sortable: false,
          value: 'issuedTo',
        },
        {
          text: this.$t('finance.invoiceAmountHeader'),
          align: 'end',
          sortable: false,
          value: 'amount',
        },
        {
          text: this.$t('finance.invoiceActionHeader'),
          value: 'action',
          sortable: false,
          align: 'end',
        },
      ],
      pageSize: 50,
      currentPage: 1,
    }
  },
  computed: {
    ...mapGetters({
      isLoadingInvoices: 'invoices/isLoading',
      invoices: 'invoices/invoices',
      invoicesTotal: 'invoices/invoicesTotal',
      currentUser: 'auth/currentUser',
      serverCurrentPage: 'invoices/serverCurrentPage',
    }),
    pageCount: function() {
      if (this.invoicesTotal <= 0) {
        return 0
      }
      return Math.floor(this.invoicesTotal / this.pageSize) + 1
    },
  },
  created: function() {
    let page = 1
    if (this.$route.query.page) {
      const pageInt = parseInt(this.$route.query.page)
      if (pageInt) {
        page = pageInt
      }
    }
    this.currentPage = page
  },
  mounted: async function() {
    await this.getInvoices({
      page: this.currentPage,
      pageSize: this.pageSize,
    })
  },
  methods: {
    ...mapActions({
      getInvoices: 'invoices/loadInvoices',
    }),
    renderIssuedTo(invoice) {
      if (invoice.groupInvoice) return invoice.groupName

      if (invoice.multipleLocations) return invoice.clientName

      return invoice.locationName
    },
    viewInvoice(invoiceId) {
      this.$router.push({ path: `/finance/invoices/${invoiceId}` })
    },
    downloadInvoice(invoiceItem) {
      this.$emit('downloadInvoice', {
        invoiceID: invoiceItem.invoiceID,
        invoiceNumber: invoiceItem.invoiceNumber,
      })
    },
    updatePage(page) {
      this.$vuetify.goTo('#invoicesTable')
      this.$router.push({ path: this.$route.path + `?page=${page}` })
    },
  },
}
</script>

<template>
  <div>
    <v-data-table
      id="invoicesTable"
      :headers="headers"
      :items="invoices"
      :items-per-page="pageSize"
      :loading="isLoadingInvoices"
      :mobile-breakpoint="960"
      :no-data-text="$t('finance.noInvoicesText')"
      :page.sync="currentPage"
      hide-default-footer
      :loading-text="$t('finance.invoicesLoadingText')"
      class="elevation-1 mt-2"
    >
      <template v-slot:[`item.paidStatus`]="{ item }">
        <span v-if="item.datePaidUTC" class="paid-status-span green--text">
          {{ $t('finance.invoicePaidText') }}
        </span>
        <span v-else class="paid-status-span red--text">
          {{ $t('finance.invoiceNotPaidText') }}
        </span>
      </template>
      <template v-slot:[`item.invoiceNumber`]="{ item }">
        <span class="font-monospace">{{ item.invoiceNumber }}</span>
      </template>
      <template v-slot:[`item.invoiceDateUTC`]="{ item }">
        <span class="font-monospace">{{
          renderDate(item.invoiceDateUTC, item.locationTimeZone)
        }}</span>
      </template>
      <template v-slot:[`item.datePaidUTC`]="{ item }">
        <span class="font-monospace">{{
          item.datePaidUTC
            ? renderDate(item.datePaidUTC, item.locationTimeZone)
            : ''
        }}</span>
      </template>
      <template v-slot:[`item.issuedTo`]="{ item }">
        <span class="issued-to-span">{{ renderIssuedTo(item) }}</span>
      </template>
      <template v-slot:[`item.amount`]="{ item }">
        <span class="amount-span font-monospace">
          {{ $n(item.exGSTAmount + item.taxAmount, 'currency') }}
        </span>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <div class="d-flex justify-end">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                dark
                small
                text
                v-bind="attrs"
                v-on="on"
                @click="downloadInvoice(item)"
              >
                <v-icon>
                  mdi-cloud-download
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('finance.invoiceDownloadButtonTip') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                dark
                small
                text
                v-bind="attrs"
                v-on="on"
                @click="viewInvoice(item.invoiceNumber)"
              >
                <v-icon>
                  mdi-text-box-search-outline
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('finance.invoiceViewButtonTip') }}</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <v-pagination
      :value="serverCurrentPage"
      class="my-4"
      :length="pageCount"
      @input="updatePage"
    ></v-pagination>
  </div>
</template>
